// Supabase 浏览器端 client 单例。仅用于 Auth（登录/会话 + Realtime 订阅 user_pulse）。
// 业务数据走 Fastify API（@doneline/sync），不直接通过 Supabase SDK 读写业务表。

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (client) return client;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error(
      'Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. Check apps/web/.env.local or .env',
    );
  }
  // Tauri webview 的同源是 tauri://localhost；deep-link URL 是通过 plugin 投递的，
  // 不会进 window.location，所以让 SDK 自己 detectSessionInUrl 在 Tauri 里没用还会噪音。
  // 浏览器 / PWA 走的是普通 magic-link 回跳，落在 https://your-site/?code=xxx，
  client = createClient(url, anonKey, {
    auth: {
      // PKCE：magic-link 回跳是 ?code=xxx，需要 exchangeCodeForSession 二次握手。
      // storage 必须显式指定 localStorage —— 之前用 @supabase/ssr 的 createBrowserClient
      // 默认走 cookies，Tauri 冷启动跨进程读不到 cookie，导致 "PKCE code verifier not
      // found in storage"。localStorage 在 WKWebView 里是真持久化的。
      flowType: 'pkce',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      storageKey: 'doneline-supabase-auth',
      persistSession: true,
      autoRefreshToken: true,
      // 恒关：?code= 兑换由 session.ts 的 initializeSupabase 手动 await（消除
      // beforeLoad 竞态），Tauri deep-link 由 useDeepLinkAuth 处理。之前浏览器分支
      // 开着它 = SDK 自动 detect 与手动 exchange 对同一 PKCE code 双重兑换，第二次
      // 必然 invalid_grant（R29 修复；code_verifier 单次消费）。
      detectSessionInUrl: false,
    },
  });
  return client;
}

export function hasSupabaseConfig(): boolean {
  return Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);
}
